<template>
  <!-- if designCode is not failed, show the design code image -->
  <div v-if="designCode && !designCodeFailed" class="gc-card-preview" :class="variantClass" :style="cardStyles">
    <v-img 
      class="gift-card-img" 
      :key="designCode" 
      :src="getDesignUrlByCode(designCode)" 
      alt="Gift Card Design"
      :style="cardStyles"
      @error="onDesignCodeError" 
    />
  </div>
  <!-- if storeRewardImage is not failed, show the store reward image -->
  <div v-else-if="storeRewardImage && !storeRewardImageFailed" class="gc-card-preview" :class="variantClass" :style="cardStyles">
    <v-img 
      @error="onStoreRewardImageError" 
      class="gift-card-img" 
      :src="storeRewardImage" 
      alt="Gift Card Design" 
      :style="cardStyles" 
    />
  </div>
  <!-- if both designCode and storeRewardImage are failed, show the default fake card -->
  <div v-else>
    <div class="gc-card-preview-fake" :class="variantClass" :style="cardStyles">
      <div class="gc-card-preview-fake-header" :style="headerStyles">
        Snappy Rewards
      </div>
      <div class="gc-card-preview-fake-name" :style="nameStyles">
        {{ getStore.name }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {get} from "lodash";
export default {
  name: "GiftCardDesign",
  props: {
    designCode: {
      type: String,
      default: null,
    },
    // Variant: 'default' for GiftCard/Confirmation, 'compact' for Checkout
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'compact'].includes(value),
    },
    // Optional: override dimensions (for backward compatibility)
    width: {
      type: Number,
      default: null,
    },
    height: {
      type: Number,
      default: null,
    },
    borderRadius: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      designCodeFailed: false,
      storeRewardImageFailed: false,
    };
  },

  watch: {
    // when designCode or storeRewardImage changes, reset the failed state
    designCode() {
      this.designCodeFailed = false;
    },
    storeRewardImage() {
      this.storeRewardImageFailed = false;
    },
  },

  methods: {
    onDesignCodeError() {
      this.designCodeFailed = true;
    },
    onStoreRewardImageError() {
      this.storeRewardImageFailed = true;
    },
  },

  computed: {
    // Variant configurations
    variantConfig() {
      const isMobile = window.innerWidth < 768;

      if (this.variant === 'compact') {
        // Checkout variant: 240x160, 10px text (same for mobile and desktop)
        return {
          width: 240,
          height: 160,
          borderRadius: 16,
          headerFontSize: 10,
          headerTop: 12,
          headerLeft: 16,
          nameFontSize: 22,
        };
      } else {
        // Default variant: GiftCard/Confirmation
        if (isMobile) {
          return {
            width: 330,
            height: 220,
            borderRadius: 20,
            headerFontSize: 14,
            headerTop: 30,
            headerLeft: 30,
            nameFontSize: 24,
          };
        } else {
          return {
            width: 540,
            height: 360,
            borderRadius: 32,
            headerFontSize: 24,
            headerTop: 40,
            headerLeft: 48,
            nameFontSize: 48,
          };
        }
      }
    },

    variantClass() {
      return `gc-card-variant-${this.variant}`;
    },

    cardStyles() {
      const config = this.variantConfig;
      return {
        width: `${this.width ?? config.width}px`,
        height: `${this.height ?? config.height}px`,
        borderRadius: `${this.borderRadius ?? config.borderRadius}px`,
      };
    },

    headerStyles() {
      const config = this.variantConfig;
      return {
        fontSize: `${config.headerFontSize}px`,
        top: `${config.headerTop}px`,
        left: `${config.headerLeft}px`,
      };
    },

    nameStyles() {
      const config = this.variantConfig;
      return {
        fontSize: `${config.nameFontSize}px`,
        textAlign: 'center',
      };
    },

    storeRewardImage() {
      const url = get(this.getStore, 'images.REWARD[0].url', null);
      if (url) {
        return `${this.origin}/v1${url}`;
      }
      return null;
    },

    ...mapGetters({
      getDesignUrlByCode: "getDesignUrlByCode",
      getStore: "getStore",
      origin: "origin",
    }),
  },
};
</script>

<style scoped>
.gc-card-preview-fake {
  overflow: hidden;
  box-shadow: 0px 0px 25px 0px #00000029;
  margin: 0 auto;
  background-blend-mode: color-dodge;
  /* Top: primary color with ~0.3 opacity, Bottom: full primary color */
  background: linear-gradient(180deg,
      color-mix(in srgb, var(--primary-color) 30%, transparent) 0%,
      var(--primary-color) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.gc-card-preview-fake-header {
  font-weight: 700;
  color: var(--primary-color);
  text-align: left;
  position: absolute;
}

.gc-card-preview-fake-name {
  font-weight: 700;
  color: var(--primary-color);
}

.gc-card-preview {
  background: #f3f4f6;
  text-align: center;
  overflow: hidden;
  margin: 0 auto;
}

.gc-card-variant-default .gc-card-preview {
  box-shadow: 0px 4.57px 9.14px 0px #00000040;
}

.gc-card-variant-compact .gc-card-preview {
  box-shadow: 0px 2.03px 4.06px 0px #00000040;
}

.gift-card-img {
  width: 100%;
  object-fit: cover;
}

/* Mobile styles for default variant only */
@media screen and (max-width: 768px) {
  .gc-card-variant-default .gc-card-preview {
    box-shadow: 0px 2.03px 4.06px 0px #00000040;
  }
}
</style>
